import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Form } from 'react-bootstrap';
import styles from './CareTeamMessage.module.css';
import { useDispatch, useSelector } from 'react-redux';
import CareTeamListGroup from './CareTeamListGroup';
import { setReloadStaffMessageList, setStaffChatList, setStaffNewChat, setStaffRecipient, setStaffSelectedIndex, setStaffChatVisible } from './CareTeamMessageSlicer';
import useAxios from '../../services/AxiosConfig';
import ChatContainer from '../../components/ChatContainer';
import { setVideoCallVisibility } from '../../components/VideoAndMessageSlice';
import { useWebSocket } from '../../services/webSocket/useWebSocket';
import { setCallInformation, openCallContainer } from '../../slicers/CallContainerSlice';


const CareTeamMessageList = ({ onCloseClick, careTeamList }) => {
    const dispatch = useDispatch();
    const axiosConfig = useSelector((state) => state.axiosConfig);
    const httpService = useAxios(axiosConfig.envURL);
    const userInfoState = useSelector((state) => state.userInfo);
    const userId = userInfoState.id;
    const userFullName = userInfoState.fullName;
    const careTeamSlicer = useSelector((state) => state.careTeam);
    const isChatVisible = careTeamSlicer.isChatVisible;
    const recipient = careTeamSlicer.recipient;
    const videoAndMessageSlicer = useSelector((state) => state.videoAndMessage);
    const isVideoCallVisible = videoAndMessageSlicer.isVideoCallVisible;
    const filterChatSlice = useSelector((state) => state.filtersChat);

    const [originalDataList, setOriginalDataList] = useState([]);

    const callContainerShowing = useSelector((state) => state.callContainer.callContainerShowing);

    const { receivedMessage } = useWebSocket();

    const searchMessages = (arr, id) => {
        return arr.flat().filter(message => message.senderId === id || message.recipientId === id);
    };

    const getAllMessageByUserId = useCallback(async () => {
        try {
            const responseData = await httpService.get(`chime/getAllMessageByUserId?userId=${userId}&recipientType=STAFF`);
            const messages = responseData.data.getAllMessage || [];
            setOriginalDataList(messages);
            dispatch(setReloadStaffMessageList(false));
        } catch (error) {
            console.error('Error fetching data:', error);
        }
    }, [userId, careTeamSlicer.reloadMessageList, dispatch, httpService]);

    useEffect(() => {
        getAllMessageByUserId();
    }, [getAllMessageByUserId]);

    //If the recipient changes make sure to set the chat reflects the change/ or handle a new message without a channel arn
    useEffect(() => {
        var newRecipient = searchMessages(originalDataList, recipient.id);
        if( newRecipient.length > 0){
            dispatch(setStaffChatList(newRecipient));
            dispatch(setStaffSelectedIndex(newRecipient[0].channelArn));
        }
    }, [recipient, originalDataList]);

    useEffect(() => {
        if (careTeamSlicer.selectedIndex !== -1) {
            const chatListMessages = originalDataList.find(array => 
                array.length > 0 && array[0].channelArn === careTeamSlicer.selectedIndex
            );
            dispatch(setStaffChatList(chatListMessages || []));
        }
    }, [careTeamSlicer.selectedIndex, originalDataList, dispatch]);

    useEffect(() => {
        if (receivedMessage || careTeamSlicer.reloadMessageList) {
            getAllMessageByUserId();
        }
    }, [receivedMessage, careTeamSlicer.reloadMessageList, getAllMessageByUserId]);

    // Refetch when user returns to this tab/window so list stays in sync with other devices
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                getAllMessageByUserId();
            }
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    }, [getAllMessageByUserId]);


    const filterMessages = (messages, searchText) => {
        const filterValue = searchText.toLowerCase().trim();
        const sortMessages = [...messages].sort((a, b) => new Date(b[0].createdTime) - new Date(a[0].createdTime));
    
        if (filterValue === '') {
            return sortMessages;
        } else {
            return sortMessages.filter(message => 
                    message.recipientName?.toLowerCase().includes(filterValue)
                );
        }
    };

    const filteredMessages = useMemo(() => {
        if (!Array.isArray(originalDataList) || originalDataList.length === 0) {
            return [];
        }
        const filtered = originalDataList.filter((item) => {
            const staffId = item[0].recipientId === userId ? item[0].senderId : item[0].recipientId;
            const staff = careTeamList.find(staff => staff.id === staffId);
            if (!staff) {
                return false;
            }

            const searchText = filterChatSlice.searchText.toLowerCase();
            const matchesSearch = staff.name.toLowerCase().includes(searchText) || item[0].message.toLowerCase().includes(searchText);

            const matchesOrganization = filterChatSlice.selectedOrganization === '' || staff['organizationIds']?.includes((Number(filterChatSlice.selectedOrganization)));
            const matchesGroup = filterChatSlice.selectedGroup === '' || staff['groupIds']?.includes(Number(filterChatSlice.selectedGroup)) || staff['groupIds']?.length === 0;
            const matchesLocation = filterChatSlice.selectedLocation === '' || staff['subGroupIds']?.includes(Number(filterChatSlice.selectedLocation)) || staff['subGroupIds']?.length === 0;
            const matchesTeam = filterChatSlice.selectedTeam === '' || staff['teamIds']?.includes(Number(filterChatSlice.selectedTeam)) || staff['teamIds']?.length === 0;;
            return matchesOrganization && matchesSearch && matchesGroup && matchesLocation && matchesTeam;

        });
        return [...filtered].sort((a, b) => new Date(b[0].createdTime) - new Date(a[0].createdTime));
    }, [careTeamList, originalDataList, filterChatSlice.searchText, filterChatSlice.selectedOrganization, filterChatSlice.selectedGroup, filterChatSlice.selectedLocation, filterChatSlice.selectedTeam]);


    const handleItemClick = async (item) => {
        if (callContainerShowing) {
            return;
        }
        dispatch(setStaffSelectedIndex(item[0].channelArn));
        dispatch(setStaffChatList(item));
        dispatch(setStaffNewChat(false));
        dispatch(setVideoCallVisibility(false));
        const messageFromUser = item.find(message => message.senderId === userId);
        if (messageFromUser) {
            dispatch(setStaffRecipient({ 'id': parseInt(messageFromUser.recipientId), name: messageFromUser.recipientName }))
        } else {
            const messageToUser = item.find(message => message.recipientId === userId);
            if (messageToUser) {
                dispatch(setStaffRecipient({ 'id': parseInt(messageToUser.senderId), name: messageToUser.senderName }));
            }
        }
        dispatch(setStaffChatVisible(true));
    };

    const handleChatContainerClose = () => {
        onCloseClick();
    };

    const handleStartCall = (audioOnly) => {
        const callData = {
            title: `Staff Message ${audioOnly ? "Audio" : "Video"} Call - ${userFullName} & ${recipient.name}`,
            appointmentId: null,
            senderId: userId,
            recipientId: recipient.id,
            senderName: userFullName,
            recipientName: recipient.name
        };
        dispatch(setStaffSelectedIndex(-1));
        dispatch(setStaffChatList([]));
        dispatch(setStaffChatVisible(false));
        dispatch(setCallInformation({"audioOnly":audioOnly, "callData":callData,viewType: "staffMessage"}));
        dispatch(openCallContainer());
    };

    const handleListStartCall = (item, audioOnly) => {
        let id, name;
        const messageFromUser = item.find(message => message.senderId === userId);
        if (messageFromUser) {
            name = messageFromUser.recipientName;
            id = parseInt(messageFromUser.recipientId);
        } else {
            const messageToUser = item.find(message => message.recipientId === userId);
            if (messageToUser) {
                name = messageToUser.senderName;
                id = parseInt(messageToUser.senderId);
            }
        }
        const callData = {
            title: `Staff Message ${audioOnly ? "Audio" : "Video"} Call - ${userFullName} & ${name}`,
            appointmentId: null,
            senderId: userId,
            recipientId: id,
            senderName: userFullName,
            recipientName: name
        };
        dispatch(setStaffSelectedIndex(-1));
        dispatch(setStaffChatList([]));
        dispatch(setStaffChatVisible(false));
        dispatch(setCallInformation({"audioOnly":audioOnly, "callData":callData,viewType: "staffMessage"}));
        dispatch(openCallContainer());
    };

    const deleteChannelMembershipRequest = async (item) => {
        const params = {
            channelArn: item[0].channelArn,
            userId: userId
        };
        try {
            await httpService.post('chime/deleteChannelMembershipRequest', params);

            // Update the state to remove the deleted conversation from the list
            const deletedChannelArn = item[0].channelArn;
            const updatedOriginalDataList = originalDataList.filter((message) => message[0].channelArn !== deletedChannelArn);
            setOriginalDataList(updatedOriginalDataList);

            // If the deleted conversation was selected, clear the chat panel
            if (careTeamSlicer.selectedIndex === deletedChannelArn) {
                dispatch(setStaffSelectedIndex(-1));
                dispatch(setStaffChatList([]));
                dispatch(setStaffChatVisible(false));
            }
        } catch (error) {
            console.error('Error deleting channel membership:', error);
        }
    };

    return (
        <div style={{flexGrow:"1", height:0, display:"flex"}}>
            <div style={{flexGrow:"1", height:"100%", overflow:"auto"}}>
                <CareTeamListGroup 
                    items={filteredMessages}
                    onStartCall={handleListStartCall}
                    onDeleteChannelMembership={deleteChannelMembershipRequest}
                    onItemClick={handleItemClick} />
            </div>
            {isChatVisible && !isVideoCallVisible && (
                <Form className={styles.ChatContainer}>
                    <ChatContainer view="staffMessage"
                        handleClose={handleChatContainerClose}
                        onStartVideoCall={() => handleStartCall(false)}
                        onStartAudioCall={() => handleStartCall(true)}
                    />
                </Form>
            )}
        </div>
    );
};

export default CareTeamMessageList;
